import React, {Component} from "react";
import {withTranslation} from "react-i18next";
import {LOCAL_STORAGE_KEYS} from "constants/AppConstants.js";
import LocalStorageService from "services/LocalStorageService.js";
import LogService from "services/LogService.js";
import {softRestart, fullReset} from "services/GlobalErrorHandler";
import {StyleDiv, StyleDivCenterChild} from "components/Common/StyleDiv";
import {StyleImg} from "components/Common/StyleImg";
import {StyleLabel} from "components/Common/StyleLabel";

interface Props {
  children?: React.ReactNode;
  t: (key: string, defaultValue?: string) => string;
}

interface State {
  error: any;
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {error: "", hasError: false};
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error: any, errorInfo: any) {
    // Every branch in this method is wrapped defensively because anything that
    // throws inside `componentDidCatch` causes React 17 to unmount the entire
    // tree and leaves a *blank* document instead of the fallback UI below. On
    // Android, `LogService.add*ErrorLogAsync` reaches into the `DiagnosticLogsPlugin`
    // Capacitor plugin which can be `null` if its dynamic import failed during
    // boot — calling `.pushLog()` on it would synchronously throw.
    let uid = null;
    try {
      uid = LocalStorageService.get(LOCAL_STORAGE_KEYS.uid);
    } catch (_) {
      // localStorage unavailable
    }

    try {
      this.setState({error});
    } catch (_) {
      // setState should not throw, but never let it take down the boundary
    }

    try {
      LogService.addGenericErrorLogAsync(`Error catched ${error}`, errorInfo?.componentStack);
    } catch (_) {
      // logging must not crash the error boundary
    }

    if (uid) {
      try {
        LogService.addErrorLogAsync(uid, "Error occured while rendering component", errorInfo?.componentStack);
      } catch (_) {
        // logging must not crash the error boundary
      }
    }

    try {
      console.error({error: error, errorInfo: errorInfo});
    } catch (_) {
      // some Android WebViews proxy console through a native bridge that can throw
    }
  }

  render() {
    const {t} = this.props;

    if (this.state.hasError) {
      const buttonBase = {
        border: "none",
        borderRadius: "var(--radius-pill)",
        padding: "12px 18px",
        fontSize: "1rem",
        fontWeight: 600,
        cursor: "pointer",
        width: "min(320px, 90vw)"
      };

      return (
        <StyleDivCenterChild backgroundColor="var(--wash)" minHeight="100vh">
          <StyleDiv margin="0px 0px 20px 0px" borderRadius="5px">
            <StyleImg height="150px" width="150px" alt="logo" src="/assets/images/waterspeed-logo.png" />
          </StyleDiv>

          <StyleLabel margin="0px 0px 20px 0px" style={{textAlign: "center", maxWidth: 480, padding: "0 16px"}}>
            {t("errors.somethingWentWrong")}
            {this.state.error ? ` ${String(this.state.error)}` : ""}
          </StyleLabel>

          <StyleDiv display="flex" style={{flexDirection: "column", gap: 10, alignItems: "center", padding: "0 16px"}}>
            <button
              type="button"
              onClick={softRestart}
              style={{...buttonBase, background: "var(--accent)", color: "var(--on-accent)"}}
            >
              {t("errors.restartApp", "Restart app")}
            </button>
            <button
              type="button"
              onClick={fullReset}
              style={{
                ...buttonBase,
                background: "transparent",
                color: "var(--danger)",
                border: "1px solid var(--danger)"
              }}
            >
              {t("errors.resetAppData", "Reset app data and restart")}
            </button>
          </StyleDiv>
        </StyleDivCenterChild>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
